import { Box } from "@mui/material";
import PageLayout from "../../components/PageLayout/PageLayout";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GetAssetSizeResponse, TrackFileType } from "../../proto/api_pb";
import { GetTrackFileResponse, TrackFile } from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { fetchFileDetails } from "../../services/fileService";
import { deleteTrackFile } from "../../services/trackService";
import { getAssetSize } from "../../services/trackService";
import { useGetUser } from "../../hooks/useGetUser";
import { month } from "../../components/TrackFile/types";
import CollabModal from "../../components/Modal/CollabModal";
import { TrackDeleteConfirmModal } from "../../components/Modal/TrackDeleteConfirmModal";
import { toast } from "react-toastify";
import { AudioFileDetails } from "../../components/FileDetails/AudioFileDetails";
import { TalkJsContext, TalkJsContextInterface } from "../../contexts";
import { ArtWorkDetails } from "../../components/FileDetails/ArtWorkDetails";
import { styles } from "./styles";

export const FilePage: () => JSX.Element = () => {
  const [fileDetails, setFileDetails] = useState<TrackFile>();
  const [fileSize, setFileSize] = useState<number>();
  const [createdDate, setCreatedDate] = useState<string>();
  const [openCollabsModal, setOpenCollabsModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [createGroupChatClicked, setCreateGroupChatClicked] = useState(false);

  const talkJsContext = useContext<TalkJsContextInterface>(TalkJsContext);
  const { user } = useGetUser(fileDetails?.getOwnerId());
  const { getValidAccessToken } = useGetValidAccessToken();
  const { id } = useParams();

  const getFileDetails = useCallback(async () => {
    const token = await getValidAccessToken();
    try {
      const response = (await fetchFileDetails(
        token,
        id!
      )) as GetTrackFileResponse;
      setFileDetails(response.getTrackFile());
      const url = response.getTrackFile()?.getFileUrl();
      if (url) {
        const fileSize = (await getAssetSize(
          token,
          url
        )) as GetAssetSizeResponse;
        const size = fileSize ? fileSize.getSizeGb() : 0;
        setFileSize(size);
        const timeStamp = response.getTrackFile()?.getTimestamp();
        if (timeStamp) {
          const date = new Date(timeStamp * 1000);
          setCreatedDate(
            `${month[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`
          );
        }
      }
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
  }, [getValidAccessToken, id]);

  useEffect(() => {
    getFileDetails();
  }, [getFileDetails]);

  const handleCloseCollabsModal = () => {
    setOpenCollabsModal(false);
  };

  const handleOpenDeleteModal = (id: string) => {
    setIsDeleteModalOpen(true);
  };
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteFile = async () => {
    if (fileDetails?.getId()) {
      const deleteTrackFileResponse = await deleteTrackFile(
        fileDetails?.getId(),
        await getValidAccessToken()
      );
      if (deleteTrackFileResponse instanceof RpcError) {
        throw deleteTrackFileResponse;
      }
      handleCloseDeleteModal();
      toast.success("File removed successfully");
    }
  };

  useEffect(() => {
    if (createGroupChatClicked && talkJsContext) {
      talkJsContext.createConversation(id!, `${fileDetails?.getFilename()}`);
    }
  }, [createGroupChatClicked, fileDetails, id, talkJsContext]);

  return (
    <>
      <TrackDeleteConfirmModal
        deleteConfirmModalOpen={isDeleteModalOpen}
        closeDeleteConfirmModal={handleCloseDeleteModal}
        handleDeleteTrack={handleDeleteFile}
        type="File"
      />
      {openCollabsModal && (
        <CollabModal
          openCollabsModal={openCollabsModal}
          handleCloseCollabsModal={handleCloseCollabsModal}
          trackToEdit={fileDetails}
        />
      )}
      <PageLayout>
        <Box sx={styles.layoutContainer}>
          {fileDetails &&
            fileDetails?.getType() !==
              TrackFileType.TRACK_FILE_TYPE_ARTWORK && (
              <AudioFileDetails
                fileDetails={fileDetails}
                user={user}
                fileSize={fileSize}
                createdDate={createdDate}
                setOpenCollabsModal={setOpenCollabsModal}
                handleOpenDeleteModal={handleOpenDeleteModal}
                talkJsContext={talkJsContext}
                setCreateGroupChatClicked={setCreateGroupChatClicked}
              />
            )}
          {fileDetails &&
            fileDetails?.getType() ===
              TrackFileType.TRACK_FILE_TYPE_ARTWORK && (
              <ArtWorkDetails
                fileDetails={fileDetails}
                user={user}
                fileSize={fileSize}
                createdDate={createdDate}
                setOpenCollabsModal={setOpenCollabsModal}
                handleOpenDeleteModal={handleOpenDeleteModal}
                talkJsContext={talkJsContext}
                setCreateGroupChatClicked={setCreateGroupChatClicked}
              />
            )}
        </Box>
      </PageLayout>
    </>
  );
};
